import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { eachDay, endOfMonth, format, getDate, getDay, getMonth, getYear, isSameDay, isSameMonth, isSameYear, isToday, setDay, startOfMonth, subDays } from 'date-fns';
import * as moment from 'moment';
import swal from 'sweetalert2';
import { Router, ActivatedRoute } from '@angular/router';
import { MultiDeviceInteractionService } from '../../../services/multi-device-interaction.service';
import { MultiDeviceInteractionState, InteractionDateModel } from '../../../models/consumer/consumer-interaction.model';


export interface DatePickerOptions {
    firstCalendarDay?: number; // 0 = Sunday (default), 1 = Monday, ..
    locale?: object;
    minDate?: Date;
    maxDate?: Date;
};

export class DatePickerModel {
    dateFrom?: Date;
};

enum DatePickerField {
    datePicker1 = 1,
};

class DayModel {
    date: Date;
    day: number;
    month: number;
    year: number;
    inThisMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isSelectable: boolean;
}

const isNil = (value: Date | DatePickerOptions | undefined) => {
    return (typeof value === 'undefined') || (value === null) || (value == undefined);
};

@Component({
    selector: 'date-picker',
    templateUrl: 'date-picker.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent), multi: true
        }
    ]
})


export class DatePickerComponent implements OnInit, OnChanges {

    @Input() isVisible = false;
    @Input() isDisabled = false;
    @Output() isVisibleChange = new EventEmitter();
    @Input() options: DatePickerOptions;
    @Output() valueChange = new EventEmitter();
    public mask = [/[0-2]/, /[0-9]|2[0-3]/, ':', /[0-5]/, /\d/]


    form: UntypedFormGroup;
    selectedDate: DatePickerModel;

    dateTime: string;
    minYear: number;
    maxYear: number;
    firstCalendarDay: number;
    dayNames: string[];
    monthNames: string[];
    years: number[];
    datePicker1_Days: DayModel[];
    subscriptions: any[] = [];
    locale: any;
    submitted: boolean = false;

    onTitleInfoClick() {
        swal({
            title: 'Select when',
            html: '<p style="text-align: left;">Choosing now will apply todays date and time to the interaction, when completed the interaction will commence immediately. Schedule for later will start this at the time scheduled.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });
    }

    constructor(
        private fb: UntypedFormBuilder,
        private multiDeviceInteractionService: MultiDeviceInteractionService,
        private router: Router,
        private route: ActivatedRoute
    ) { }

    get dateFromString(): string {
        if (!this.form.value.dateFrom) return null;
        return moment(this.form.value.dateFrom).format('Do MMM YYYY');
    }

    ngOnInit() {
        this.form = this.fb.group({
            'isScheduled': ["false"],
            'dateFrom': [null, Validators.compose([Validators.required])],
            'dateTime': [null, Validators.compose([Validators.required])],
            'datePicker1_Month': [null],
            'datePicker1_Year': [null]
        });

        this.reset();
        this.setOptions();

        this.update();

        this.form.controls.isScheduled.setValue(false);
        this.changeType();
        this.update();

        this.subscriptions.push(this.form.controls.dateTime.valueChanges.subscribe((timeVal: string) => {

            if (timeVal && timeVal.length > 2 && Number(timeVal.charAt(0)) == 2 &&
                (Number(timeVal.charAt(1)) > 3 || Number(timeVal.charAt(1)) == 0)) {
                let currentVal = timeVal.replace(timeVal.charAt(1), '');
                this.form.controls.dateTime.setValue(currentVal);
            }

            if (timeVal && timeVal.length === 5 && this.selectedDate && this.selectedDate.dateFrom) {

                let clonedDate = this.selectedDate.dateFrom;

                let timeParts = timeVal.split(':');
                let fromDate = this.form.get('dateFrom').value as Date;

                clonedDate.setHours(Number(timeParts[0]));
                clonedDate.setMinutes(Number(timeParts[1]));

                fromDate = clonedDate;

                fromDate.setHours(Number(timeParts[0]));
                fromDate.setMinutes(Number(timeParts[1]));
                this.form.controls.dateFrom.setValue(fromDate);
                this.form.updateValueAndValidity();

                // recreate date object to force update
                this.selectedDate.dateFrom = new Date(clonedDate.getFullYear(), clonedDate.getMonth(), clonedDate.getDate(), clonedDate.getHours(), clonedDate.getMinutes());
            }

        }))

        // init day names
        this.dayNames = [];
        const start = this.firstCalendarDay;
        for (let i = start; i <= 6 + start; i++) {
            const date = setDay(new Date(), i);
            let dayName = format(date, 'ddd', this.locale);
            this.dayNames.push(dayName);
        }

        // init month names
        this.monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        if (this.multiDeviceInteractionService.whenModel) {

            this.form.get('isScheduled').setValue(this.multiDeviceInteractionService.whenModel.isScheduled);
            let storedDate = new Date(this.multiDeviceInteractionService.whenModel.date);
            if (storedDate.getTimezoneOffset() == -60) {
                storedDate.setHours(storedDate.getHours() - 1);
            }
            let storedTime = this.addZero(storedDate.getHours()) + ':' + this.addZero(storedDate.getMinutes());

            if (this.multiDeviceInteractionService.whenModel.isScheduled) {

                this.form.get('dateTime').setValue(storedTime);
                this.form.value.dateFrom = storedDate;
                this.update();

            } else {

                let currentDate = new Date();
                if (currentDate.getTimezoneOffset() == -60) {
                    storedDate.setHours(currentDate.getHours() - 1);
                }
                let newTime = this.addZero(currentDate.getHours()) + ':' + this.addZero(currentDate.getMinutes());
                this.form.get('dateTime').setValue(newTime);
                this.form.value.dateFrom = currentDate;
            }
        }

        // init years dropdown
        const range = this.maxYear - this.minYear;
        this.years = Array.from(new Array(range), (x, i) => i + this.minYear);
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('options' in changes) {
            this.setOptions();
        }

        if ('isVisible' in changes) {
            if (!this.isVisible) return;
            this.update();
        }
    }

    reset() {
        this.form.reset();
        let dateFrom = this.selectedDate ? this.selectedDate.dateFrom : new Date();

        if (this.selectedDate) {
            this.form.controls.dateFrom.setValue(dateFrom)
        }

        this.form.controls.datePicker1_Month.setValue(dateFrom.getMonth())
        this.form.controls.datePicker1_Year.setValue(dateFrom.getFullYear())
    }

    setOptions() {
        const today = new Date();

        this.minYear = this.options && this.options.minDate ?
            this.options.minDate.getFullYear() : getYear(today) - 30;
        this.maxYear = this.options && this.options.maxDate ?
            this.options.maxDate.getFullYear() + 1 : getYear(today) + 30;

        this.firstCalendarDay = this.options && this.options.firstCalendarDay || 0;
        this.locale = this.options && { locale: this.options.locale } || {};
    }

    nextMonth(field: DatePickerField) {
        let dateFrom = this.form.value.dateFrom;

        let datePicker1_Month = +this.form.value.datePicker1_Month;
        let datePicker1_Year = +this.form.value.datePicker1_Year;

        if (datePicker1_Month < 11) {
            this.form.controls.datePicker1_Month.setValue(datePicker1_Month + 1);
        } else {
            this.form.controls.datePicker1_Month.setValue(0);
            this.form.controls.datePicker1_Year.setValue(datePicker1_Year + 1);
        }

        this.form.controls.dateFrom.setValue(dateFrom);
        this.updateMonthYear(field);
    }

    prevMonth(field: DatePickerField) {
        let dateFrom = this.form.value.dateFrom;

        let datePicker1_Month = +this.form.value.datePicker1_Month;
        let datePicker1_Year = +this.form.value.datePicker1_Year;

        if (datePicker1_Month > 0) {
            this.form.controls.datePicker1_Month.setValue(datePicker1_Month - 1);
        } else {
            this.form.controls.datePicker1_Month.setValue(11);
            this.form.controls.datePicker1_Year.setValue(datePicker1_Year - 1);
        }

        this.form.controls.dateFrom.setValue(dateFrom);
        this.updateMonthYear(field);
    }

    updateMonthYear(field: DatePickerField) {
        let dateFrom = this.form.value.dateFrom;

        let datePicker1_Month = +this.form.value.datePicker1_Month;
        let datePicker1_Year = +this.form.value.datePicker1_Year;

        this.form.controls.datePicker1_Year.setValue(datePicker1_Year);

        this.form.controls.dateFrom.setValue(dateFrom);
        this.update();
    }

    selectDate(field: DatePickerField, i: number) {
        if (this.form.value.dateFrom) {
            this.form.controls.dateFrom.setValue(null);
        }
        this.form.controls.isScheduled.setValue(true);
        this.form.value.dateFrom = this.datePicker1_Days[i].date;
        this.update();
    }

    update() {
        let dateFrom = this.form.value.dateFrom;
        let dateTime = this.form.value.dateTime;

        const datePicker1_Start = startOfMonth(new Date(this.form.value.datePicker1_Year, this.form.value.datePicker1_Month, 1));
        const datePicker1_End = endOfMonth(new Date(this.form.value.datePicker1_Year, this.form.value.datePicker1_Month, 1));

        let isSelected = (date: Date): boolean => {
            let isSelected = false;
            if (this.form.value.dateFrom &&
                isSameDay(date, this.form.value.dateFrom) &&
                isSameMonth(date, this.form.value.dateFrom) &&
                isSameYear(date, this.form.value.dateFrom)) {
                isSelected = true;
            }
            return isSelected;
        }

        let isSelectable = (date: Date): boolean => {
            if (isNil(this.options)) return true;
            const isMinDateSet = !isNil(this.options.minDate);
            const isMaxDateSet = !isNil(this.options.maxDate);
            const timestamp = date.valueOf();
            if (isMinDateSet && (timestamp < this.options.minDate!.valueOf())) return false;
            if (isMaxDateSet && (timestamp > this.options.maxDate!.valueOf())) return false;
            return true;
        }

        this.datePicker1_Days = eachDay(datePicker1_Start, datePicker1_End).map(date => {
            return {
                date: date,
                day: getDate(date),
                month: getMonth(date),
                year: getYear(date),
                inThisMonth: true,
                isToday: isToday(date),
                isSelected: isSelected(date),
                isSelectable: isSelectable(date)
            };
        }).slice();

        const tmpStart = getDay(datePicker1_Start) - this.firstCalendarDay;
        const prevDaysStart = tmpStart < 0 ? 7 - this.firstCalendarDay : tmpStart;

        for (let i = 1; i <= prevDaysStart; i++) {
            const date = subDays(datePicker1_Start, i);
            this.datePicker1_Days.unshift({
                date: date,
                day: getDate(date),
                month: getMonth(date),
                year: getYear(date),
                inThisMonth: false,
                isToday: isToday(date),
                isSelected: isSelected(date),
                isSelectable: isSelectable(date)
            });
        }

        this.datePicker1_Days = this.datePicker1_Days.slice();
        this.form.controls.dateFrom.setValue(dateFrom);

        this.selectedDate = {
            dateFrom: dateFrom,
        };

        if (this.dateTimeCheck(dateTime) && this.selectedDate.dateFrom) {
            let dateParts = dateTime.split(':');
            this.selectedDate.dateFrom.setHours(Number(dateParts[0]));
            this.selectedDate.dateFrom.setMinutes(Number(dateParts[1]));
            this.form.controls.dateFrom.setValue(this.selectedDate.dateFrom);
        }

        this.valueChange.emit(this.selectedDate);
    }

    changeType() {
        let isScheduled = this.form.controls.isScheduled.value;

        if (!isScheduled) {
            let currentTimeString = "";

            let currentHours = new Date().getHours();
            let currentMinutes = new Date().getMinutes();

            currentTimeString = this.addZero(currentHours) + ':' + this.addZero(currentMinutes);

            this.form.controls.dateFrom.setValue(new Date());
            this.selectedDate.dateFrom = new Date();

            this.selectedDate.dateFrom.setHours(Number(currentHours));
            this.selectedDate.dateFrom.setMinutes(Number(currentMinutes));
            this.form.controls.dateTime.setValue(currentTimeString);

        } else {
            let currentDate = new Date();
            if (this.datePicker1_Days.find(x => x.day == currentDate.getDay())) {
                currentDate.setDate(currentDate.getDate() + 1)
                this.form.value.dateFrom = currentDate;
                this.selectedDate.dateFrom = currentDate;
                let currentTimeString = this.addZero(currentDate.getHours()) + ':' + this.addZero(currentDate.getMinutes());

                this.form.controls.dateTime.setValue(currentTimeString);
            }
        }

        this.update();
    }

    close() {
        this.isVisible = false;
        this.isVisibleChange.emit(this.isVisible);
        this.reset();
    }

    clear() {
        this.selectedDate = null;
        this.valueChange.emit(this.selectedDate);
        this.close();
    }

    dateTimeCheck(time: string): boolean {
        if (!time || time === "") return false;

        let timeParts = time.split(':');

        if (Number(timeParts[0]) > 23) {
            return false;
        }

        return true;
    }

    submitForm(form: UntypedFormGroup) {
        this.submitted = true;
        let dateFrom = this.form.value.dateFrom;
        let fullTime = this.form.value.dateTime;

        if (!this.dateTimeCheck(fullTime)) return;

        this.selectedDate = {
            dateFrom: dateFrom
        };

        this.valueChange.emit(this.selectedDate);

        //set when model
        let currentInteractionModel = this.multiDeviceInteractionService.getModelMultiDeviceModel;
        if (!currentInteractionModel.when) { currentInteractionModel.when = new InteractionDateModel(); }

        if (this.form.controls.isScheduled.value == true) {
            let timeParts = fullTime.split(':');
            currentInteractionModel.when.date = this.selectedDate.dateFrom;
            //account for british daylight savings
            if (this.isBST(currentInteractionModel.when.date)) {
                let selectedHours = currentInteractionModel.when.date.getHours();
                if ((selectedHours + 1 > 23)) {
                    currentInteractionModel.when.date.setHours(0);

                } else {
                    currentInteractionModel.when.date.setHours(selectedHours + 1);
                }
            }
        } else {
            currentInteractionModel.when.date = null;
        }

        currentInteractionModel.when.isScheduled = this.form.controls.isScheduled.value;
        window.sessionStorage.setItem('multi-device-interaction', JSON.stringify(currentInteractionModel));

        let currentState = MultiDeviceInteractionState.when;
        let nextState = this.multiDeviceInteractionService.getNextState(currentState);

        this.router.navigated = false;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { state: nextState }
        });
    }

    addZero(i) {
        i < 10 ? i = "0" + i : i;
        return i;
    }


    lastSunday(month, year) {
        let d = new Date();
        let lastDayOfMonth = new Date(Date.UTC(year || d.getFullYear(), month + 1, 0));
        let day = lastDayOfMonth.getDay();
        return new Date(Date.UTC(lastDayOfMonth.getFullYear(), lastDayOfMonth.getMonth(), lastDayOfMonth.getDate() - day));
    }

    isBST(date) {
        var d = date || new Date();
        var starts = this.lastSunday(2, d.getFullYear());
        starts.setHours(1);
        var ends = this.lastSunday(9, d.getFullYear());
        ends.setHours(1);
        return d.getTime() >= starts.getTime() && d.getTime() < ends.getTime();
    }



}
